/**
 * Chain-first reward menu sync.
 *
 * Resolves the caller's ShopCapability for a given on-chain shop, signs
 * `set_reward_tiers`, and executes it — BEFORE the reward menu is persisted
 * to Supabase. This keeps the displayed menu in lockstep with what
 * `redeem_reward` validates on-chain (see coffee_shop_registry.move).
 */
import { fetchShopCapabilities, buildSetRewardTiersTx, buildSetCardSizeTx } from './sui-service';
import { signAndExecuteTransaction } from './zklogin-signer';
import { normalizeAddress } from '../lib/shop-address';
import type { RewardTier } from './shop-service';

export type RewardSyncResult =
  | { ok: true }
  | { ok: false; reason: 'shop-not-onchain' | 'no-capability' | 'tx-failed' };

export async function syncRewardTiersOnChain(params: {
  shopOnchainId: string | null;
  ownerAddress: string;
  tiers: RewardTier[];
}): Promise<RewardSyncResult> {
  const { shopOnchainId, ownerAddress, tiers } = params;

  if (!shopOnchainId) {
    return { ok: false, reason: 'shop-not-onchain' };
  }

  try {
    const capabilities = await fetchShopCapabilities(ownerAddress);
    const target = normalizeAddress(shopOnchainId);
    const capability = capabilities.find((obj: any) => {
      const shopId = obj?.data?.content?.fields?.shop_id;
      return typeof shopId === 'string' && normalizeAddress(shopId) === target;
    });

    if (!capability?.data?.objectId) {
      return { ok: false, reason: 'no-capability' };
    }

    const tx = buildSetRewardTiersTx(shopOnchainId, capability.data.objectId, tiers);
    await signAndExecuteTransaction(tx);
    return { ok: true };
  } catch (err) {
    console.error('[reward-sync] set_reward_tiers failed:', err);
    return { ok: false, reason: 'tx-failed' };
  }
}

/**
 * Push a shop's card length on chain.
 *
 * Deliberately does NOT resolve a ShopCapability the way syncRewardTiersOnChain
 * does — `set_card_size` authorises on the SIGNER (`sender == shop_owner`), not
 * on a capability object, so looking one up would be a pointless RPC round trip
 * that fails for owners whose capability has moved.
 */
export async function setCardSizeOnChain(params: {
  shopOnchainId: string | null;
  freeEveryN: number;
}): Promise<RewardSyncResult> {
  const { shopOnchainId, freeEveryN } = params;

  if (!shopOnchainId) {
    return { ok: false, reason: 'shop-not-onchain' };
  }

  try {
    const tx = buildSetCardSizeTx(shopOnchainId, freeEveryN);
    await signAndExecuteTransaction(tx);
    return { ok: true };
  } catch (err) {
    console.error('[reward-sync] set_card_size failed:', err);
    return { ok: false, reason: 'tx-failed' };
  }
}
